import React, { useRef, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

interface AutoScrollCardsProps {
  children: React.ReactNode[];
  className?: string;
}

const AutoScrollCards: React.FC<AutoScrollCardsProps> = ({ children, className = '' }) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const { ref: inViewRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  useEffect(() => {
    let animationFrameId: number;
    const scrollSpeed = 1; // pixels per frame

    const animate = () => {
      if (!isScrolling || !scrollContainerRef.current) return;

      const container = scrollContainerRef.current;
      container.scrollLeft += scrollSpeed;

      // Reset to start when reaching the end
      if (container.scrollLeft >= container.scrollWidth - container.clientWidth) {
        container.scrollLeft = 0;
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    if (inView && !isDragging) {
      setIsScrolling(true);
      animationFrameId = requestAnimationFrame(animate);
    } else {
      setIsScrolling(false);
    }

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [inView, isDragging, isScrolling]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setIsScrolling(false);
    setStartX(e.pageX - scrollContainerRef.current!.offsetLeft);
    setScrollLeft(scrollContainerRef.current!.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => {
      if (inView) {
        setIsScrolling(true);
      }
    }, 100);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current!.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current!.scrollLeft = scrollLeft - walk;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setIsScrolling(false);
    setStartX(e.touches[0].pageX - scrollContainerRef.current!.offsetLeft);
    setScrollLeft(scrollContainerRef.current!.scrollLeft);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setTimeout(() => {
      if (inView) {
        setIsScrolling(true);
      }
    }, 100);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - scrollContainerRef.current!.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current!.scrollLeft = scrollLeft - walk;
  };

  return (
    <div 
      ref={inViewRef}
      className="relative overflow-hidden"
    >
      <div
        ref={scrollContainerRef}
        className={`auto-scroll-container ${className}`}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
      >
        <div className="auto-scroll-content">
          {children}
          {/* Duplicate children for seamless infinite scroll */}
          {children}
        </div>
      </div>
    </div>
  );
};

export default AutoScrollCards;